import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Static styles moved outside component to prevent recreation
const cellStyles = {
  container: "flex flex-col w-full min-h-[40px] overflow-hidden",
  input:
    "w-full border border-transparent bg-transparent focus:ring-2 focus:ring-blue-500 rounded p-1 text-sm",
  inputError: "!border-red-500",
  error: "text-xs text-red-600 h-4 mt-0.5 truncate",
  readOnlyText: "truncate text-sm px-1 py-1 min-h-[24px] flex items-center",
};

const TableCell = React.memo(
  React.forwardRef(
    ({ value = "", onChange, columnKey, error, isEditing = true }, ref) => {
      const inputRef = ref || useRef(null);
      const [inputValue, setInputValue] = useState(value);
      const isEditingRef = useRef(false);
      const previousValueRef = useRef(value);

      // Update input value when external value changes and not actively editing
      useEffect(() => {
        if (!isEditingRef.current) {
          setInputValue(value);
          previousValueRef.current = value;
        }
      }, [value]);

      // Handle input change with debounce for better performance
      const handleChange = useCallback((e) => {
        const newValue = e.target.value;
        isEditingRef.current = true;
        setInputValue(newValue);
      }, []);

      // Save changes - only called on blur or Enter key
      const saveChanges = useCallback(() => {
        isEditingRef.current = false;

        // Only trigger onChange if value actually changed
        if (inputValue !== previousValueRef.current) {
          onChange(columnKey, inputValue);
          previousValueRef.current = inputValue;
        }
      }, [inputValue, onChange, columnKey]);

      const handleBlur = useCallback(() => {
        saveChanges();
      }, [saveChanges]);

      // Handle Enter key to save changes and move to next field
      const handleKeyDown = useCallback(
        (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            saveChanges();

            // Try to move to next input field
            const form = inputRef.current?.form;
            if (form) {
              const inputs = Array.from(form.querySelectorAll("input"));
              const currentIndex = inputs.indexOf(inputRef.current);
              if (currentIndex < inputs.length - 1) {
                inputs[currentIndex + 1].focus();
              }
            }
          } else if (e.key === "Escape") {
            // Revert changes on Escape
            e.preventDefault();
            isEditingRef.current = false;
            setInputValue(previousValueRef.current);
          }
        },
        [saveChanges, inputRef],
      );

      // Focus the input when it becomes editable
      useEffect(() => {
        if (isEditing && inputRef.current) {
          inputRef.current.focus();
        }
      }, [isEditing]);

      // Memoize the input element to prevent unnecessary re-renders
      const inputElement = useMemo(() => {
        if (!isEditing) {
          return (
            <div 
              className={cellStyles.readOnlyText} 
              title={inputValue || 'Boş değer'}
            >
              {inputValue || "—"}
            </div>
          );
        }

        return (
          <input
            ref={inputRef}
            type="text"
            className={`${cellStyles.input} ${error ? cellStyles.inputError : ""}`}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={!isEditing}
            aria-invalid={!!error}
            aria-describedby={error ? `error-${columnKey}` : undefined}
            placeholder="Değer girin..."
          />
        );
      }, [
        inputValue,
        isEditing,
        handleChange,
        handleBlur,
        handleKeyDown,
        inputRef,
        error,
        columnKey,
      ]);

      return (
        <div className={cellStyles.container}>
          <div className="flex flex-1 items-center w-full">
            {inputElement}
          </div>
          {error && (
            <div className={cellStyles.error} id={`error-${columnKey}`}>
              {error}
            </div>
          )}
        </div>
      );
    },
  ),
  // Custom comparison function for React.memo
  (prevProps, nextProps) => {
    // Only re-render if necessary props change
    return (
      prevProps.value === nextProps.value &&
      prevProps.isEditing === nextProps.isEditing &&
      prevProps.error === nextProps.error &&
      prevProps.columnKey === nextProps.columnKey
    );
  },
);

// Set display name for better debugging
TableCell.displayName = "TableCell";

export default TableCell;
